/**
 * Pure view helpers for the three decisions a staged batch is waiting on (f8.3).
 *
 * **Nothing here counts anything the server counted.** `outstanding` arrives on
 * the role-slots read, computed from the same snapshot the rows came from, and
 * `batch_state` arrives beside it. A browser that re-derived "is this
 * committable?" from a list length would be a second opinion, and the day the
 * two disagree is the day a batch commits on the forty-second of forty-three
 * resolutions. Everything below either FORMATS a server value or answers a
 * question about presentation.
 *
 * **No role vocabulary either.** Role labels come from f2.2's `DELIVERY_ROLE`
 * catalogue at runtime, exactly as the ownership cards read them; the fallback
 * is the code itself, so a role added in a later phase renders rather than
 * crashing.
 */

import type {
  ContestedSlot,
  Job,
  LookupValue,
  Outstanding,
  RoleHolderDecision,
} from '@/api/client';
import { isJobTerminal } from '@/api/queries';

/** The only batch state a commit is accepted from. The server's rule, mirrored. */
export const COMMITTABLE_STATE = 'STAGED';

/** The states whose plan may still be decided. */
export const DECIDABLE_STATES: readonly string[] = ['STAGED', 'AWAITING_RESOLUTION'];

export function isDecidable(state: string): boolean {
  return DECIDABLE_STATES.includes(state);
}

/**
 * The delivery role's name, from the live catalogue.
 *
 * The code when the catalogue has not loaded or has no such row: a chip
 * reading `DEM` is honest, and one reading nothing is a hole.
 */
export function roleLabel(code: string, roles: readonly LookupValue[]): string {
  return roles.find((role) => role.code === code)?.name ?? code;
}

/**
 * The banner's title: how many DISTINCT HUMANS are unresolved.
 *
 * 43 on the real workbook, never 148. The prototype's *"7 role holders matched
 * no Atlas user"* is illustrative; the shape of the sentence is not.
 */
export function unresolvedNamesTitle(count: number): string {
  return count === 1
    ? '1 role holder matched no Atlas user'
    : `${count} role holders matched no Atlas user`;
}

/**
 * The other two decisions, named separately in the same banner.
 *
 * Separately because they are different questions with different controls: a
 * contested slot is not an unmatched name, and merging the counts would make
 * the operator hunt for a control the number does not point at.
 */
export function otherDecisions(state: Outstanding): string[] {
  const parts: string[] = [];
  if (state.contested_slots > 0) {
    parts.push(
      state.contested_slots === 1
        ? '1 record the workbook names two holders for'
        : `${state.contested_slots} records the workbook names more than one holder for`,
    );
  }
  if (state.codes > 0) {
    parts.push(
      state.codes === 1 ? '1 code that collided' : `${state.codes} codes that collided`,
    );
  }
  return parts;
}

/**
 * The `card-foot` progress line.
 *
 * *"0 of 43 resolved -- commit stays disabled until all are handled"*, with the
 * total being names the server folded and the resolved count being the
 * difference. Both come from one payload, so they cannot disagree.
 */
export function resolutionProgress(
  names: readonly RoleHolderDecision[],
  outstandingNames: number,
): string {
  const total = names.length;
  const done = total - outstandingNames;
  if (total === 0) return 'No role holder needed a decision';
  if (outstandingNames === 0) {
    return `${total} of ${total} resolved`;
  }
  return `${done} of ${total} resolved — commit stays disabled until all are handled`;
}

/**
 * Whether the commit job is still moving.
 *
 * `isJobTerminal` is f8.1's -- the same predicate the progress hook stops
 * polling on -- so the button and the bar cannot disagree about whether the
 * commit has finished.
 */
export function isCommitRunning(job: Job | undefined): boolean {
  return job !== undefined && !isJobTerminal(job.state);
}

/**
 * Why `Commit import` is disabled, or `null` when it is not.
 *
 * A REASON rather than a boolean, so the control can say what it is waiting
 * for instead of merely being grey. The button mirrors the server: the batch
 * state decides, and a running commit job is the transient condition no eighth
 * batch state was seeded for (design D4).
 *
 * This is presentation. The server refuses a commit from any state but
 * `STAGED` regardless of what this returns.
 */
export function commitBlockedReason(
  batchState: string,
  outstanding: Outstanding | null | undefined,
  commitJob: Job | undefined,
): string | null {
  if (isCommitRunning(commitJob)) return 'The commit is running.';
  if (batchState === 'COMMITTED') return 'This batch has already been committed.';
  if (batchState === COMMITTABLE_STATE) return null;
  if (outstanding && outstanding.total > 0) {
    return 'Every decision above has to be made first.';
  }
  return `A batch that is ${readableState(batchState)} cannot be committed.`;
}

function readableState(state: string): string {
  return state.toLowerCase().replace(/_/g, ' ');
}

/**
 * The `Appears on` summary for one NAME -- the nodes, not a slot's single one.
 *
 * Three names and a remainder: the full list for `Axel Apice` is fifteen nodes
 * and would push every other column off the row. The count is the truth and
 * the sample is the orientation.
 */
export function appearsOnSummary(decision: RoleHolderDecision): string {
  const nodes = decision.appears_on;
  if (nodes.length === 0) return '—';
  const shown = nodes.slice(0, 3).join(', ');
  const rest = nodes.length - 3;
  return rest > 0 ? `${shown} and ${rest} more` : shown;
}

/**
 * The sentence the assign dialog shows before the operator commits to it.
 *
 * The prototype's *"All 4 Project Head assignments carried by this name will be
 * granted to the chosen account"*, generalised over every role the name holds
 * -- because `Axel Apice` holds five and a sentence naming one would understate
 * the decision by fourteen slots.
 */
export function fanOutNotice(
  decision: RoleHolderDecision,
  roles: readonly LookupValue[],
): string {
  const parts = decision.roles.map(
    (occupancy) =>
      `${occupancy.slot_count} ${roleLabel(occupancy.delivery_role_code, roles)}`,
  );
  const listed =
    parts.length <= 1
      ? (parts[0] ?? '0')
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  const noun = decision.slot_count === 1 ? 'assignment' : 'assignments';
  return `All ${listed} ${noun} carried by this name will be granted to the chosen account.`;
}

/**
 * The evidence line under a contested candidate.
 *
 * *"named on 7 of 10 engagement rows"* -- a COUNT, and deliberately never a
 * recommendation. Thirteen of the fourteen real contests have a strict
 * majority and one does not, so a system that endorsed the leader would be
 * right thirteen times and silently wrong once, on the largest business unit
 * in the estate.
 */
export function evidenceLabel(sourceRowCount: number, totalRows: number): string {
  const rows = totalRows === 1 ? 'row' : 'rows';
  return `named on ${sourceRowCount} of ${totalRows} source ${rows}`;
}

/** A contested candidate's display name: the account's, or the raw spelling. */
export function candidateName(
  candidate: ContestedSlot['candidates'][number],
): string {
  return candidate.bound_user?.display_name ?? candidate.raw_name;
}

/**
 * The initials for an avatar. Two letters, from the first and last word.
 *
 * Duplicated from neither screen: it is three lines, and importing the Users
 * screen's copy is exactly the dependency f8.3 is removing elsewhere.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]![0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Whether a search term matches a candidate account.
 *
 * Name AND e-mail, because two GFT colleagues genuinely share display names
 * and the address is what tells the operator which human they are naming.
 */
export function matchesSearch(
  displayName: string,
  email: string,
  term: string,
): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (
    displayName.toLowerCase().includes(needle) || email.toLowerCase().includes(needle)
  );
}
